import { useEffect, useRef, useState, useCallback } from "react";
import * as maplibregl from "maplibre-gl";
import { getCategoryColor } from "../utils/CategoryColors";

type Props = {
  map: maplibregl.Map | null;
  inspeksiData: GeoJSON.FeatureCollection<GeoJSON.Point, any>;
  onMarkerClick?: (feature: any) => void;
};

const EXPLOSION_RADIUS = 50; //70; // Dalam pixel
const ANIMATION_DURATION = 300;
const CLUSTER_DETECTION_TOLERANCE = 10; //15;

const ClusterExploder = ({ map, inspeksiData, onMarkerClick }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [positions, setPositions] = useState<Array<{
    original: [number, number];
    exploded: [number, number];
    feature: any;
  }>>([]);
  const [clusterCenter, setClusterCenter] = useState<[number, number]>([0, 0]);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const calculateExplodedPositions = useCallback((features: any[], center: [number, number]) => {
    if (!map) return [];
    
    const centerPixel = map.project(center);
    const angleStep = (2 * Math.PI) / features.length;
    
    return features.map((feature, index) => {
      const angle = index * angleStep;
      const explodedPixel: [number, number] = [
        centerPixel.x + EXPLOSION_RADIUS * Math.cos(angle),
        centerPixel.y + EXPLOSION_RADIUS * Math.sin(angle)
      ];
      
      return {
        original: center,
        exploded: explodedPixel,
        feature
      };
    });
  }, [map]);

  const getClusterCenter = (features: any[]): [number, number] => {
    const coordinates = features.map(f => f.geometry.coordinates);
    return [
      coordinates.reduce((sum, c) => sum + c[0], 0) / coordinates.length,
      coordinates.reduce((sum, c) => sum + c[1], 0) / coordinates.length
    ];
  };

  const handleMapClick = useCallback((e: maplibregl.MapMouseEvent & maplibregl.EventData) => {
    if (!map) return;
  
    const features = map.queryRenderedFeatures(e.point, {
      layers: ['inspeksi-marker'],
    });
  
    if (!features.length) return;
  
    const nearbyFeatures = map.queryRenderedFeatures([
      [e.point.x - CLUSTER_DETECTION_TOLERANCE, e.point.y - CLUSTER_DETECTION_TOLERANCE],
      [e.point.x + CLUSTER_DETECTION_TOLERANCE, e.point.y + CLUSTER_DETECTION_TOLERANCE]
    ], {
      layers: ['inspeksi-marker'],
    });
  
    if (nearbyFeatures.length > 1) {
      // HENTIKAN event bubbling!
      if (e.originalEvent) {
        e.originalEvent.stopPropagation();
        e.originalEvent.preventDefault();
      }
      const center = getClusterCenter(nearbyFeatures);
      const newPositions = calculateExplodedPositions(nearbyFeatures, center);
      setClusterCenter(center);
      setPositions(newPositions);
      setVisible(true);
    } else {
      // Kalau hanya 1 marker
      if (onMarkerClick) {
        onMarkerClick(features[0]);
      }
    }
  }, [map, calculateExplodedPositions, onMarkerClick]);
  

  const handleClose = useCallback(() => {
    setVisible(false);
    setPositions([]);
  }, []);

  useEffect(() => {
    if (!map) return;

    map.on('click', handleMapClick);

    return () => {
      map.off('click', handleMapClick);
    };
  }, [map, handleMapClick]);

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (visible && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) {
        handleClose();
      }
    };

    document.addEventListener('click', handleDocumentClick);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, handleClose]);

  if (!visible || positions.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      onClick={handleClose}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'auto',
        opacity: 1,
        transition: `opacity ${ANIMATION_DURATION}ms ease-out`,
        zIndex: 1000,
        transformOrigin: 'center center'
      }}
    >
      {/* Connection lines */}
      {positions.map((pos, index) => {
        const originalPixel = map?.project(pos.original) || { x: 0, y: 0 };
        const lineAngle = Math.atan2(
          pos.exploded[1] - originalPixel.y,
          pos.exploded[0] - originalPixel.x
        );

        return (
          <div
            key={`line-${index}`}
            style={{
              position: 'absolute',
              left: `${originalPixel.x}px`,
              top: `${originalPixel.y}px`,
              width: `${Math.hypot(
                pos.exploded[0] - originalPixel.x,
                pos.exploded[1] - originalPixel.y
              )}px`,
              height: '2px',
              background: '#666',
              opacity: 0.6,
              transform: `rotate(${lineAngle}rad)`,
              transformOrigin: '0 50%',
              pointerEvents: 'none'
            }}
          />
        );
      })}

      {/* Exploded markers */}
      {positions.map((pos, index) => (
        <div
          key={`marker-${index}`}
          style={{
            position: 'absolute',
            left: `${pos.exploded[0]}px`,
            top: `${pos.exploded[1]}px`,
            transform: 'translate(-50%, -50%)',
            zIndex: 1000
          }}
        >
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: getCategoryColor(pos.feature.properties?.category),
              border: '2px solid white',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              transition: 'transform 0.2s ease-out'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onMarkerClick?.(pos.feature);
              handleClose();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default ClusterExploder;